<template>
  <v-container>
    <v-responsive
      class="align-center mx-auto"
      max-width="1000"
    >
      <h1 class="text-h4 font-weight-black pt-8 pb-4 pl-3">
        Link Budget Calculator
      </h1>

      <v-card
        class="rounded-xl"
        elevation="0"
      >
        <v-card-text class="pa-6">
          <p class="text-body-1 text-medium-emphasis mb-8">
            Calculate the link budget for your wireless network by providing the required parameters
            below. This tool helps determine the feasibility of your wireless connection based on
            distance and environmental factors.
          </p>

          <v-form
            ref="form"
            v-model="isFormValid"
            @submit.prevent="handleCalculate"
          >
            <v-row>
              <v-col
                v-if="showError"
                cols="12"
              >
                <v-alert
                  color="error"
                  icon="mdi-alert-circle"
                  title="Error"
                  variant="tonal"
                  border="start"
                  closable
                  @click:close="showError = false"
                >
                  {{ errorMessage }}
                </v-alert>
              </v-col>

              <v-col cols="12">
                <v-select
                  v-model="selectedLinkType"
                  label="What is the link type?"
                  :items="['Point-to-point', 'Point-to-multi-point']"
                  variant="outlined"
                  :rules="[v => !!v || 'Link type is required']"
                  required
                />
              </v-col>

              <v-col cols="12">
                <v-row>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <div class="device-container">
                      <div class="device-header">
                        <v-icon
                          icon="mdi-antenna"
                          color="primary"
                          class="me-2"
                        />
                        <span class="text-subtitle-1 font-weight-medium">Transmitter Configuration</span>
                      </div>
                      <div class="device-content">
                        <v-row>
                          <v-col cols="8">
                            <v-select
                              v-model="selectedTxModel"
                              label="Choose transmitter device"
                              :items="deviceModels"
                              variant="outlined"
                              :rules="[v => !!v || 'Transmitter device is required']"
                              required
                            />
                          </v-col>
                          <v-col cols="4">
                            <div class="checkbox-wrapper">
                              <v-checkbox
                                v-model="txDishMode"
                                label="Dish Mode"
                                color="primary"
                                hide-details
                                density="comfortable"
                                :disabled="selectedTxModel !== 'PRM2141'"
                                @update:model-value="value => !value && (txDishGain = 0)"
                              />
                            </div>
                          </v-col>
                          <v-col cols="6">
                            <v-text-field 
                              v-model="txRadomeLoss"
                              label="Radome Loss (dB)" 
                              variant="outlined"
                              type="number"
                              density="comfortable"
                              @update:model-value="value => txRadomeLoss = Number(value)"
                            />
                          </v-col>
                          <v-col cols="6">
                            <v-text-field 
                              v-model="txDishGain"
                              label="Dish Gain (dBi)" 
                              variant="outlined"
                              type="number"
                              :disabled="!txDishMode"
                              density="comfortable"
                              @update:model-value="value => txDishGain = Number(value)"
                            />
                          </v-col>
                        </v-row>
                      </div>
                    </div>
                  </v-col>

                  <v-col
                    cols="12"
                    md="6"
                  >
                    <div class="device-container">
                      <div class="device-header">
                        <v-icon
                          icon="mdi-access-point"
                          color="primary"
                          class="me-2"
                        />
                        <span class="text-subtitle-1 font-weight-medium">Receiver Configuration</span>
                      </div>
                      <div class="device-content">
                        <v-row>
                          <v-col cols="8">
                            <v-select
                              v-model="selectedRxModel"
                              label="Choose receiver device"
                              :items="deviceModels"
                              variant="outlined"
                              :rules="[v => !!v || 'Receiver device is required']"
                              required
                            />
                          </v-col>
                          <v-col cols="4">
                            <div class="checkbox-wrapper">
                              <v-checkbox
                                v-model="rxDishMode"
                                label="Dish Mode"
                                color="primary"
                                hide-details
                                density="comfortable"
                                :disabled="selectedRxModel !== 'PRM2141'"
                                @update:model-value="value => !value && (rxDishGain = 0)"
                              />
                            </div>
                          </v-col>
                          <v-col cols="6">
                            <v-text-field 
                              v-model="rxRadomeLoss"
                              label="Radome Loss (dB)" 
                              variant="outlined"
                              type="number"
                              density="comfortable"
                              @update:model-value="value => rxRadomeLoss = Number(value)"
                            />
                          </v-col>
                          <v-col cols="6">
                            <v-text-field 
                              v-model="rxDishGain"
                              label="Dish Gain (dBi)" 
                              variant="outlined"
                              type="number"
                              :disabled="!rxDishMode"
                              density="comfortable"
                              @update:model-value="value => rxDishGain = Number(value)"
                            />
                          </v-col>
                        </v-row>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-col>

              <v-col
                cols="12"
                md="6"
                lg="6"
              >
                <v-select
                  v-model="selectedChannel"
                  label="Select a Channel"
                  :items="[
                    'Channel 1 (58,320MHz)',
                    'Channel 2 (60,480MHz)',
                    'Channel 3 (62,640MHz)',
                    'Channel 4 (64,800MHz)',
                    'Channel 5 (66,960MHz)',
                    'Channel 6 (69,120MHz)'
                  ]"
                  variant="outlined"
                  :rules="[v => !!v || 'Channel is required']"
                  required
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
                lg="6"
              >
                <v-select
                  v-model="selectedBandwidth"
                  label="Select Channel Bandwidth"
                  :items="['Full Bandwidth']"
                  variant="outlined"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
                lg="6"
              >
                <v-select
                  v-model="selectedRegion"
                  label="Choose Region"
                  :items="regions.map((r) => r.name)"
                  variant="outlined"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
                lg="6"
              >
                <v-select
                  v-model="selectedRainZone"
                  label="Rain Zone"
                  :items="availableRainZones"
                  variant="outlined"
                  :disabled="!selectedRegion"
                  :rules="[v => !!v || 'Access Point device is required']"
                  required
                  @update:model-value="handleRainZoneChange"
                />
              </v-col>

              <v-col cols="12">
                <div class="distance-container">
                  <v-row align="center">
                    <v-col
                      cols="12"
                      sm="4"
                      md="3"
                    >
                      <v-text-field
                        v-model.number="distance"
                        label="Distance"
                        variant="outlined"
                        type="number"
                        :min="0"
                        :rules="[
                          v => v !== null && v !== '' || 'Distance is required',
                          v => v > 0 || 'Distance must be greater than zero'
                        ]"
                        required
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="4"
                      md="3"
                    >
                      <v-btn-toggle
                        v-model="distanceUnit"
                        color="primary"
                        variant="outlined"
                        mandatory
                        divided
                        class="unit-toggle"
                      >
                        <v-btn
                          value="m"
                          size="large"
                        >
                          meters
                        </v-btn>
                        <v-btn
                          value="km"
                          size="large"
                        >
                          km
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                    <v-col
                      cols="12"
                      sm="4"
                      md="6"
                    >
                      <div class="d-flex align-center">
                        <span class="text-caption text-medium-emphasis me-3">Quick:</span>
                        <v-chip-group
                          selected-class="text-primary"
                          class="flex-grow-1"
                        >
                          <v-chip
                            v-for="preset in distancePresets"
                            :key="preset.label"
                            variant="outlined"
                            color="primary"
                            size="small"
                            @click="applyPreset(preset)"
                          >
                            {{ preset.label }}
                          </v-chip>
                        </v-chip-group>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-col>

              <v-col cols="12">
                <div class="mcs-selector mt-4">
                  <div class="text-subtitle-1 font-weight-medium mb-4 text-medium-emphasis">
                    Target Modulation:
                  </div>
                  <v-radio-group
                    v-model="targetModulation"
                    inline
                    density="compact"
                    class="mcs-radio-group"
                    hide-details
                  >
                    <v-radio
                      v-for="mcs in [1, 4, 6, 9, 10, 11, 12]"
                      :key="mcs"
                      :label="`MCS ${mcs}\n${mcsThroughputMap[mcs]}`"
                      :value="mcs"
                      class="mcs-radio"
                    />
                  </v-radio-group>
                </div>
              </v-col>

              <v-col cols="12">
                <v-expansion-panels variant="accordion">
                  <v-expansion-panel>
                    <v-expansion-panel-title>
                      <h3 class="text-h6 font-weight-regular">
                        Additional Configuration
                      </h3>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <v-checkbox
                        v-model="fccMode"
                        label="Enforce EIRP Limit of 40dBm (FCC/CE)"
                        color="primary"
                        hide-details
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>

              <v-col
                cols="12"
                class="d-flex justify-end"
              >
                <v-btn
                  color="primary"
                  variant="outlined"
                  size="large"
                  type="button"
                  width="200"
                  class="me-4"
                  @click="resetForm"
                >
                  <v-icon start>
                    mdi-refresh
                  </v-icon>
                  Reset
                </v-btn>
                <v-btn
                  color="primary"
                  variant="flat"
                  size="large"
                  width="200"
                  :loading="isCalculating"
                  type="submit"
                >
                  <v-icon start>
                    mdi-calculator
                  </v-icon>
                  Calculate
                </v-btn>
              </v-col>

              <v-col
                v-if="showResults"
                ref="resultsSection"
                cols="12"
              >
                <v-alert
                  :color="calculationResult?.connection ? 'success' : 'warning'"
                  :icon="calculationResult?.connection ? 'mdi-check-circle' : 'mdi-alert'"
                  title="Link Budget Results"
                  variant="tonal"
                  border="start"
                >
                  <div class="d-flex flex-column gap-2">
                    <div class="text-subtitle-2">
                      <span class="font-weight-medium">Distance: </span>
                      <span class="text-medium-emphasis">{{ distance }} {{ distanceUnit }}</span>
                    </div>
                    <div class="text-subtitle-2">
                      <span class="font-weight-medium">RX Sensitivity: </span>
                      <span class="text-medium-emphasis">{{ calculationResult?.rx_sensitivity }} dBm</span>
                    </div>
                    <div class="text-subtitle-2">
                      <span class="font-weight-medium">Expected RSSI: </span>
                      <span class="text-medium-emphasis">{{ calculationResult?.expectedRSSI }} dBm</span>
                    </div>
                    <div class="text-subtitle-2">
                      <span class="font-weight-medium">Quality of Service: </span>
                      <span class="text-medium-emphasis">{{ calculationResult?.qos }}</span>
                    </div>
                  </div>
                </v-alert>
              </v-col>

              <v-col
                v-if="showResults && calculationResult?.p2pGraphData?.length"
                cols="12"
              >
                <McsRangeChart :data="calculationResult.p2pGraphData" />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </v-responsive>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useLinkBudgetStore } from '@/stores/link_budget/link-budget'
import type { ILinkBudgetPayloadData } from '@/services/link_budget/link_budget.types'
import { getRainValue } from '@/models/rain_fade/rain-fade'
import type { VForm } from 'vuetify/components'
import McsRangeChart from './components/McsRangeChart.vue'

const linkBudgetStore = useLinkBudgetStore()

const deviceModels = ['PRM2141', 'PRM2142', 'PRM2143', 'PRM2144', 'PRM2146', 'PRM2136']

const distance = ref<number>(0)
const distanceUnit = ref<'m' | 'km'>('m')
const selectedRegion = ref<string | null>(null)
const selectedLinkType = ref<string | null>(null)
const selectedRxModel = ref<string | null>(null)
const selectedTxModel = ref<string | null>(null)
const selectedChannel = ref<string | null>(null)
const selectedRainZone = ref<string | null>(null)
const selectedAvailability = ref<string>('99.9%')
const rainAmount = ref<number>(0)
const selectedBandwidth = ref<string>('Full Bandwidth')

const showResults = ref(false)
const calculationResult = computed(() => linkBudgetStore.result)

const showError = ref(false)
const errorMessage = ref('')

const form = ref<VForm | null>(null)
const isFormValid = ref(false)
const resultsSection = ref<{ $el: HTMLElement } | null>(null)

const rxDishMode = ref(false)
const txDishMode = ref(false)
const fccMode = ref(false)

const rxRadomeLoss = ref(0)
const txRadomeLoss = ref(0)
const rxDishGain = ref(0)
const txDishGain = ref(0)

const targetModulation = ref(1)

const distancePresets = [
  { label: '100m', value: 100, unit: 'm' as const },
  { label: '500m', value: 500, unit: 'm' as const },
  { label: '1 km', value: 1, unit: 'km' as const },
  { label: '1.5 km', value: 1.5, unit: 'km' as const },
  { label: '2 km', value: 2, unit: 'km' as const },
]

// Flag to prevent auto-conversion when applying presets
const isApplyingPreset = ref(false)

const applyPreset = (preset: typeof distancePresets[number]) => {
  isApplyingPreset.value = true
  distanceUnit.value = preset.unit
  distance.value = preset.value
  // Reset flag on next tick to allow normal conversions again
  setTimeout(() => {
    isApplyingPreset.value = false
  }, 0)
}

// Convert distance to meters for API
const distanceInMeters = computed(() => {
  const numericDistance = Number(distance.value)
  if (distanceUnit.value === 'km') {
    return numericDistance * 1000
  }
  return numericDistance
})

// Mapping visible values to API values
const linkTypeMap = {
  'Point-to-point': 'PtP',
  'Point-to-multi-point': 'PtMP'
}

const channelMap = {
  'Channel 1 (58,320MHz)': 1,
  'Channel 2 (60,480MHz)': 2,
  'Channel 3 (62,640MHz)': 3,
  'Channel 4 (64,800MHz)': 4,
  'Channel 5 (66,960MHz)': 5,
  'Channel 6 (69,120MHz)': 6
}

const modelApiMap: Record<string, string> = {
  PRM2146: 'PRM2146D'
}

// const bandwidthMap = {
//   'Full Bandwidth': 'full',
//   'Half Bandwidth': 'half'
// }

const mcsThroughputMap: Record<number, string> = {
  0: 'No Link',
  1: '330 Mbps',
  2: '660 Mbps',
  3: '820 Mbps',
  4: '1000 Mbps',
  5: '1090 Mbps',
  6: '1340 Mbps',
  7: '1650 Mbps',
  8: '1990 Mbps',
  9: '2130 Mbps',
  10: '2580 Mbps',
  11: '2960 Mbps',
  12: '3000 Mbps',
}

const regions = [
  {
    name: 'All Regions',
    rainZones: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L', 'M', 'N', 'P']
  },
  { name: 'North America', rainZones: ['B', 'C', 'D', 'E'] },
  { name: 'South America', rainZones: ['N', 'P', 'K', 'M'] },
  { name: 'Europe', rainZones: ['E', 'H', 'K'] },
  { name: 'Africa', rainZones: ['J', 'K', 'N', 'P'] },
  { name: 'Asia', rainZones: ['E', 'F', 'G', 'K', 'M', 'N'] },
  { name: 'Oceania', rainZones: ['F', 'J', 'P'] }
]

selectedRegion.value = 'All Regions'

const availableRainZones = computed(() => {
  const region = regions.find((r) => r.name === selectedRegion.value)
  return region?.rainZones || []
})

// Add new ref for loading state
const isCalculating = ref(false)

const handleCalculate = async () => {
  try {
    const validation = await form.value?.validate()
    
    if (!validation?.valid) {
      throw new Error('Please fill in all required fields')
    }

    isCalculating.value = true
    showResults.value = false
    showError.value = false

    const payload: ILinkBudgetPayloadData = {
      linkType: (linkTypeMap as Record<string, string>)[selectedLinkType.value!],
      APModel: modelApiMap[selectedRxModel.value!] ?? selectedRxModel.value!,
      APEnableDish: rxDishMode.value,
      APDishGain: rxDishGain.value,
      scanType: "BORESIGHT",
      APRadomeLoss: rxRadomeLoss.value,
      STAModel: modelApiMap[selectedTxModel.value!] ?? selectedTxModel.value!,
      STAEnableDish: txDishMode.value,
      STAEnableFCC: fccMode.value,
      STADishGain: txDishGain.value,
      STARadomeLoss: txRadomeLoss.value,
      targetDistance: distanceInMeters.value,
      distanceUnit: 'm',
      rainZone: selectedRainZone.value!,
      targetModulation: targetModulation.value,
      channel: (channelMap as Record<string, number>)[selectedChannel.value!],
      // bandWidth: (bandwidthMap as Record<string, string>)[selectedBandwidth.value!],
    }
    await linkBudgetStore.calculateLinkBudget(payload)
    showResults.value = true
    await nextTick()
    resultsSection.value?.$el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'An error occurred while calculating the link budget'
    showError.value = true
  } finally {
    isCalculating.value = false
  }
}

// Auto-convert distance when unit changes (but not when applying presets)
watch(distanceUnit, (newUnit, oldUnit) => {
  if (isApplyingPreset.value) return
  
  if (oldUnit && distance.value && distance.value > 0) {
    if (oldUnit === 'm' && newUnit === 'km') {
      distance.value = Number((distance.value / 1000).toFixed(3))
    } else if (oldUnit === 'km' && newUnit === 'm') {
      distance.value = Number((distance.value * 1000).toFixed(0))
    }
  }
})

// Add watchers for device model changes
watch(selectedRxModel, (newValue) => {
  if (newValue !== 'PRM2141' && rxDishMode.value) {
    rxDishMode.value = false
    rxDishGain.value = 0
  }
})

watch(selectedTxModel, (newValue) => {
  if (newValue !== 'PRM2141' && txDishMode.value) {
    txDishMode.value = false
    txDishGain.value = 0
  }
})

const resetForm = () => {
  form.value?.reset()
  showResults.value = false
  showError.value = false
  linkBudgetStore.resetCalculation()
  
  distance.value = 0
  distanceUnit.value = 'm'
  selectedRegion.value = 'All Regions'
  selectedLinkType.value = null
  selectedRxModel.value = null
  selectedTxModel.value = null
  selectedChannel.value = null
  selectedRainZone.value = null
  selectedAvailability.value = '99.9%'
  rainAmount.value = 0
  selectedBandwidth.value = 'Full Bandwidth'
  rxDishMode.value = false
  txDishMode.value = false
  fccMode.value = false
  rxRadomeLoss.value = 0
  txRadomeLoss.value = 0
  rxDishGain.value = 0
  txDishGain.value = 0
  targetModulation.value = 1
}

const handleRainZoneChange = (zone: string | null) => {
  if (zone) {
    const qos = 0.1 // default to 99.9%
    rainAmount.value = getRainValue(zone, qos)
    selectedAvailability.value = '99.9%'
  }
}

// TODO: Temporarily commenting until support is added
/*
// Add QoS mapping
const availabilityQosMap = {
  '99%': 1,
  '99.7%': 0.3,
  '99.9%': 0.1,
  '99.97%': 0.03,
  '99.99%': 0.01,
  '99.997%': 0.003,
  '99.999%': 0.001
}

const handleAvailabilityChange = (availability: string | null) => {
  if (availability && selectedRainZone.value) {
    const qos = availabilityQosMap[availability as keyof typeof availabilityQosMap]
    rainAmount.value = getRainValue(selectedRainZone.value, qos)
  }
}
*/
</script>

<style scoped>
:deep(.v-field) {
  border-radius: 8px;
}

:deep(.v-alert) {
  border-radius: 8px;
}

:deep(.v-card) {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.distance-container {
  border: 1px solid rgba(var(--v-border-color), 0.12);
  border-radius: 8px;
  padding: 24px;
  background-color: rgb(var(--v-theme-surface));
}

.unit-toggle {
  width: 100%;
  height: 56px;
  border-radius: 8px;
}

.unit-toggle :deep(.v-btn) {
  flex: 1;
  height: 100%;
  font-weight: 500;
  letter-spacing: 0.0892857143em;
  text-transform: none;
}

.device-container {
  border: 1px solid rgba(var(--v-border-color), 0.12);
  border-radius: 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: rgb(var(--v-theme-surface));
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease-in-out;
}

.device-container:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.device-header {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(var(--v-border-color), 0.12);
  background-color: rgb(var(--v-theme-surface));
  display: flex;
  align-items: center;
  border-radius: 12px 12px 0 0;
}

.device-content {
  padding: 24px;
  background-color: rgb(var(--v-theme-surface));
  border-radius: 0 0 12px 12px;
}

.checkbox-wrapper {
  height: 56px;
  display: flex;
  align-items: center;
}

:deep(.v-checkbox) {
  margin: 0;
  padding: 0;
}

.mcs-selector {
  border: 1px solid rgba(var(--v-border-color), 0.12);
  border-radius: 8px;
  padding: 24px;
  background-color: rgb(var(--v-theme-surface));
}

.mcs-radio-group {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 32px;
}

:deep(.mcs-radio) {
  margin: 0;
  flex: 1;
  min-width: 120px;
  
  .v-label {
    white-space: pre-line;
    text-align: center;
    line-height: 1.4;
    font-size: 0.875rem;
    font-weight: 400;
    letter-spacing: 0.15px;
    font-family: "Roboto", sans-serif;
    opacity: var(--v-high-emphasis-opacity);
  }

  .v-selection-control {
    min-width: 100%;
  }
}

:deep(.v-radio-group) {
  width: 100%;
}
</style>
  